'use client'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FaPause, FaPlay } from 'react-icons/fa'
import AppStoreLink from '../AppStoreLink'

/**
 * AppTour — animated slideshow of the real App Store screenshots.
 *
 * Carousel discipline (the reason this isn't a classic auto-carousel):
 *  - Slides are labelled tabs with benefit copy, never anonymous dots —
 *    users can see and reach every slide directly.
 *  - Auto-advance only runs while the section is in the viewport, pauses
 *    on hover/focus, respects prefers-reduced-motion, and STOPS for good
 *    the moment the user picks a slide themselves (their intent wins).
 *  - An explicit pause/play control satisfies WCAG 2.2.2 (hover-pause
 *    alone isn't enough for keyboard/touch users).
 *  - The screenshot itself is an App Store link — screenshots double as
 *    CTAs across the page.
 */

const SLIDES = [
  {
    id: 'today',
    src: '/screens/today.webp',
    label: 'Today',
    title: 'Your exposure at a glance',
    body: 'The exposure gauge, your rooms, and today’s quest. One screen that tells you where you stand and what to do next.',
    alt: 'MicroPlastics Today screen with exposure gauge, room summaries, and daily quest',
  },
  {
    id: 'scan-log',
    src: '/screens/scan-log.webp',
    label: 'Scan log',
    title: 'Every scan, scored and saved',
    body: 'A 0–100 risk score for each product, with the research behind it and a safer swap when the score runs high.',
    alt: 'MicroPlastics scan log screen with scored products',
  },
  {
    id: 'rooms',
    src: '/screens/rooms.webp',
    label: 'Rooms',
    title: 'Map your home, room by room',
    body: 'Kitchen, bathroom, laundry: audit each room and watch its exposure tier improve as you swap the worst offenders.',
    alt: 'MicroPlastics rooms screen showing per-room exposure audit',
  },
  {
    id: 'quests',
    src: '/screens/quests.webp',
    label: 'MicroQuests',
    title: 'Scanning, turned into missions',
    body: 'Guided scavenger hunts like the Kitchen Plastic Hunt: 10-minute missions that end in a badge and a passport stamp.',
    alt: 'MicroPlastics quests screen with quest library',
  },
  {
    id: 'passport',
    src: '/screens/passport.webp',
    label: 'Passport',
    title: 'Stamp your Plastic Passport',
    body: 'Every distinct plastic source you uncover earns a stamp. Fill pages, share cards, and see how much you’ve mapped.',
    alt: 'MicroPlastics Plastic Passport screen with collected stamps',
  },
  {
    id: 'reports',
    src: '/screens/reports.webp',
    label: 'Reports',
    title: 'Proof your swaps are working',
    body: 'Weekly and monthly exposure reports with period-over-period comparisons. Real data, not vibes.',
    alt: 'MicroPlastics reports screen with exposure trends',
  },
] as const

const AUTO_ADVANCE_MS = 5000

export default function AppTour() {
  const prefersReducedMotion = useReducedMotion()
  const [active, setActive] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [inView, setInView] = useState(false)
  const [hovered, setHovered] = useState(false)
  const userTookOverRef = useRef(false)
  const sectionRef = useRef<HTMLElement>(null)

  // Only animate while visible — off-screen autoplay burns main-thread time
  // and advances the deck no one is watching.
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const shouldAutoPlay = playing && inView && !hovered && !prefersReducedMotion

  useEffect(() => {
    if (!shouldAutoPlay) return
    const t = setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length)
    }, AUTO_ADVANCE_MS)
    return () => clearInterval(t)
  }, [shouldAutoPlay])

  const select = useCallback((i: number) => {
    // A deliberate pick ends the slideshow — never fight the user's choice.
    userTookOverRef.current = true
    setPlaying(false)
    setActive(i)
  }, [])

  const slide = SLIDES[active]

  return (
    <section
      ref={sectionRef}
      className="w-full bg-deepForest py-16 sm:py-section-lg overflow-hidden"
      aria-labelledby="app-tour-heading"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="frame">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-goldLight mb-3">
            Inside the app
          </p>
          <h2
            id="app-tour-heading"
            className="font-display text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-cream leading-tight mb-3"
          >
            Six screens, one habit
          </h2>
          <p className="text-base sm:text-lg text-cream/70 leading-relaxed">
            Straight from the current iOS build. Tap any screen to get the app.
          </p>
        </div>

        {/* grid-cols-1 (minmax(0,1fr)) is load-bearing on mobile: without an
            explicit mobile column, the implicit `auto` track sizes to the
            caption's max-content (one unwrapped line), overflowing the viewport
            and pushing the phone off-centre. md: restores the desktop layout. */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:gap-14 items-center">
          {/* ── Tab list (desktop: vertical rail; mobile: scrollable chips) ── */}
          <div>
            {/* Mobile chips */}
            <div
              className="md:hidden flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-4"
              role="tablist"
              aria-label="App screens"
            >
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={i === active}
                  onClick={() => select(i)}
                  className={`shrink-0 px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight ${
                    i === active
                      ? 'bg-goldLight text-deepForest'
                      : 'bg-white/[0.07] text-cream/70 hover:text-cream'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Mobile caption for the active slide */}
            <div className="md:hidden text-center mb-6" aria-live="polite">
              <h3 className="text-lg font-bold text-cream mb-1">{slide.title}</h3>
              <p className="text-sm text-cream/70 leading-relaxed">{slide.body}</p>
            </div>

            {/* Desktop rail */}
            <div className="hidden md:block" role="tablist" aria-label="App screens">
              {SLIDES.map((s, i) => {
                const isActive = i === active
                return (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => select(i)}
                    className={`group relative w-full text-left rounded-2xl px-5 py-4 mb-1.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight ${
                      isActive ? 'bg-white/[0.07]' : 'hover:bg-white/[0.04]'
                    } pressable-sm`}
                  >
                    <span className="flex items-baseline gap-3">
                      <span
                        className={`font-display text-sm font-bold tabular-nums transition-colors duration-200 ${
                          isActive ? 'text-goldLight' : 'text-cream/35'
                        }`}
                        aria-hidden="true"
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">
                        <span className={`block font-bold text-base transition-colors duration-200 ${isActive ? 'text-cream' : 'text-cream/60 group-hover:text-cream/85'}`}>
                          {s.title}
                        </span>
                        {/* Body copy renders only on the active row so the rail
                            stays scannable, six open paragraphs would be noise. */}
                        <AnimatePresence initial={false}>
                          {isActive && (
                            <motion.span
                              className="block text-sm text-cream/65 leading-relaxed overflow-hidden"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1, marginTop: 4 }}
                              exit={{ height: 0, opacity: 0, marginTop: 0 }}
                              transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
                            >
                              {s.body}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </span>
                    </span>
                    {/* Auto-advance progress hairline on the active row */}
                    {isActive && shouldAutoPlay && (
                      <span className="absolute left-5 right-5 bottom-1.5 h-px bg-white/10 overflow-hidden" aria-hidden="true">
                        <motion.span
                          key={`${active}-progress`}
                          className="absolute inset-y-0 left-0 bg-goldLight/70"
                          initial={{ width: '0%' }}
                          animate={{ width: '100%' }}
                          transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
                        />
                      </span>
                    )}
                  </button>
                )
              })}

              {/* Pause/play — explicit control, not hover-only (WCAG 2.2.2). */}
              <button
                type="button"
                onClick={() => {
                  userTookOverRef.current = true
                  setPlaying((p) => !p)
                }}
                className="mt-3 ml-5 inline-flex items-center gap-2 text-xs font-semibold text-cream/50 hover:text-cream transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-goldLight rounded-lg px-2 py-1.5"
                aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
              >
                {playing ? <FaPause className="w-2.5 h-2.5" aria-hidden="true" /> : <FaPlay className="w-2.5 h-2.5" aria-hidden="true" />}
                {playing ? 'Pause' : 'Play'}
              </button>
            </div>
          </div>

          {/* ── Device frame — one stable frame, screens crossfade inside ── */}
          <div className="flex justify-center md:justify-end">
            <AppStoreLink
              source="home_app_tour"
              trackingParams={{ page_type: 'home', slide: slide.id }}
              aria-label={`Download MicroPlastics on the App Store, showing the ${slide.label} screen`}
              className="block group"
            >
              <div className="relative w-[240px] sm:w-[270px] md:w-[290px] aspect-[880/1912] rounded-[2.6rem] border-[6px] border-black/60 bg-ink shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden transition-transform duration-300 group-hoverable:scale-[1.015]">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={slide.id}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.45, ease: 'easeOut' }}
                  >
                    <Image
                      src={slide.src}
                      alt={slide.alt}
                      width={880}
                      height={1912}
                      sizes="(max-width: 640px) 240px, 290px"
                      className="w-full h-auto rounded-[2.2rem]"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </AppStoreLink>
          </div>
        </div>

        {/* Preload every slide so crossfades never flash an unloaded image.
            `loading="eager"` is required: lazy images inside display:none
            never load (no intersection), which would defeat the preload. */}
        <div className="hidden" aria-hidden="true">
          {SLIDES.map((s) => (
            <Image key={s.id} src={s.src} alt="" width={880} height={1912} loading="eager" />
          ))}
        </div>
      </div>
    </section>
  )
}
